import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import toast from 'react-hot-toast';
import App from './App.jsx';
import { store } from './app/store';
import { listenToAuthChanges } from './app/authListener';
import { installGlobalErrorReporting } from './utils/errorReporter';
import { installFileDropNavigationGuard } from './utils/preventFileDropNavigation';
import { captureAttribution } from './utils/attribution';
import './index.css';
import { HelmetProvider } from 'react-helmet-async';
// react-quill's stylesheet is NOT imported here: it was loading the rich-text
// editor's CSS into the global stylesheet on every page, and all four pages
// that actually render an editor (Contact, NewTicket, TicketDetails,
// AdminSupportConsole) already import it themselves.
import reportWebVitals from './reportWebVitals';
import { register as registerSW, unregister as unregisterSW } from './serviceWorkerRegistration';

// Pin how this visitor arrived before anything can navigate. document.referrer
// starts reporting our own origin the moment React Router moves, and the UTM
// tags only exist on the URL they actually landed on — after that both are gone.
captureAttribution();

// Initialize auth listener
listenToAuthChanges(store);

// Capture uncaught client errors / promise rejections for the admin error log
installGlobalErrorReporting();

// A file dropped anywhere the app didn't expect one makes the browser navigate
// the tab to display it — silently destroying whatever was being worked on,
// with nothing logged anywhere. Covers every screen; only file drags are
// cancelled, so dragging text into an input still behaves normally.
installFileDropNavigationGuard();

reportWebVitals(({ id, name, value }) => {
  window.gtag?.('event', name, {
    event_category: 'Web Vitals',
    event_label: id,
    value: Math.round(name === 'CLS' ? value * 1000 : value),
    non_interaction: true,
  });
});

// Only run the service worker in production builds. In local dev a SW serves stale
// bundles and fights HMR, causing endless reloads — so unregister it there instead.
if (process.env.NODE_ENV === 'production') {
  registerSW({
    // The service worker's own install handler already calls skipWaiting() and
    // clients.claim() unconditionally, so a new version takes over network
    // requests within moments of deploying — but that does NOT touch the React
    // app already running in this tab's memory. A creator who had the app open
    // before a deploy stays on the old JS indefinitely, silently, with no sign
    // anything changed, until they happen to hard-refresh or close the tab.
    // That's exactly the gap that made today's dashboard fixes look like they
    // hadn't landed even after being deployed and verified live. This is the
    // one missing piece: tell them, and let them choose when to reload rather
    // than yanking the page out from under something they're mid-typing.
    onUpdate: () => {
      toast(
        (t) => React.createElement(
          'div',
          { className: 'flex items-center gap-3' },
          React.createElement('span', null, 'A new version of MintLink is available.'),
          React.createElement(
            'button',
            {
              onClick: () => window.location.reload(),
              className: 'px-3 py-1 rounded-lg bg-[#32764A] text-white text-sm font-semibold flex-shrink-0',
            },
            'Refresh'
          )
        ),
        { duration: Infinity, id: 'sw-update' }
      );
    },
  });
} else {
  unregisterSW();
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
     <HelmetProvider>
    <Provider store={store}>
      <App />
    </Provider>
     </HelmetProvider>

  </React.StrictMode>
);